/**
 * End-to-end encryption of the chat history carried over a device switch. The target makes
 * a P-256 key pair per switch and sends only the public half; the source encrypts with
 * ECDH + HKDF-SHA256 + AES-256-GCM, so the server stores and relays ciphertext only.
 * The mobile app implements the same scheme and must pass the same test vectors.
 */

export const CHAT_CRYPTO_VERSION = 1;
export const CHAT_CRYPTO_ALG = 'P256-HKDF-A256GCM';

const HKDF_INFO_LABEL = 'convo-switch-chat-v1';
const IV_BYTES = 12;

export interface ChatKeyPair {
    /** Uncompressed P-256 point (65 bytes), base64. */
    publicKey: string;
    privateKey: CryptoKey;
}

interface EncryptedChatHistory {
    v: number;
    alg: string;
    epk: string;
    iv: string;
    ct: string;
}

/** Test hook: fixes the source key pair and IV so the output is reproducible. */
export interface EncryptOverrides {
    ephemeralKeyPair?: CryptoKeyPair;
    iv?: Uint8Array;
}

const ECDH_PARAMS: EcKeyImportParams = { name: 'ECDH', namedCurve: 'P-256' };

const encoder = new TextEncoder();
const decoder = new TextDecoder();

export function bytesToBase64(bytes: Uint8Array): string {
    let binary = '';
    const chunk = 0x8000;
    for (let i = 0; i < bytes.length; i += chunk) {
        binary += String.fromCharCode(...bytes.subarray(i, i + chunk));
    }
    return btoa(binary);
}

export function base64ToBytes(base64: string): Uint8Array {
    const binary = atob(base64);
    const bytes = new Uint8Array(binary.length);
    for (let i = 0; i < binary.length; i++) {
        bytes[i] = binary.charCodeAt(i);
    }
    return bytes;
}

function concat(...parts: Uint8Array[]): Uint8Array {
    const out = new Uint8Array(parts.reduce((length, part) => length + part.length, 0));
    let offset = 0;
    parts.forEach((part) => {
        out.set(part, offset);
        offset += part.length;
    });
    return out;
}

async function importPublicKey(raw: Uint8Array): Promise<CryptoKey> {
    return crypto.subtle.importKey('raw', raw, ECDH_PARAMS, true, []);
}

/** Both public keys go into the HKDF info, binding the AES key to this exact pair. */
async function deriveAesKey(
    privateKey: CryptoKey,
    peerPublicKey: CryptoKey,
    sourcePublicRaw: Uint8Array,
    targetPublicRaw: Uint8Array,
    usage: KeyUsage,
): Promise<CryptoKey> {
    const sharedSecret = await crypto.subtle.deriveBits(
        { name: 'ECDH', public: peerPublicKey },
        privateKey,
        256,
    );
    const hkdfKey = await crypto.subtle.importKey('raw', sharedSecret, 'HKDF', false, [
        'deriveKey',
    ]);

    return crypto.subtle.deriveKey(
        {
            name: 'HKDF',
            hash: 'SHA-256',
            salt: new Uint8Array(0),
            info: concat(encoder.encode(HKDF_INFO_LABEL), sourcePublicRaw, targetPublicRaw),
        },
        hkdfKey,
        { name: 'AES-GCM', length: 256 },
        false,
        [usage],
    );
}

/** Target side, at accept. The private key is non-extractable and lives in memory only. */
export async function generateChatKeyPair(): Promise<ChatKeyPair> {
    const keyPair = await crypto.subtle.generateKey(ECDH_PARAMS, false, ['deriveBits']);
    const publicRaw = new Uint8Array(await crypto.subtle.exportKey('raw', keyPair.publicKey));

    return { publicKey: bytesToBase64(publicRaw), privateKey: keyPair.privateKey };
}

/** Source side. [requestId] is the AES-GCM associated data. */
export async function encryptChatHistory(
    plaintext: string,
    targetPublicKey: string,
    requestId: string,
    overrides: EncryptOverrides = {},
): Promise<string> {
    const targetPublicRaw = base64ToBytes(targetPublicKey);
    const ephemeral =
        overrides.ephemeralKeyPair ??
        (await crypto.subtle.generateKey(ECDH_PARAMS, true, ['deriveBits']));
    const sourcePublicRaw = new Uint8Array(
        await crypto.subtle.exportKey('raw', ephemeral.publicKey),
    );
    const aesKey = await deriveAesKey(
        ephemeral.privateKey,
        await importPublicKey(targetPublicRaw),
        sourcePublicRaw,
        targetPublicRaw,
        'encrypt',
    );
    const iv = overrides.iv ?? crypto.getRandomValues(new Uint8Array(IV_BYTES));
    const ciphertext = await crypto.subtle.encrypt(
        { name: 'AES-GCM', iv, additionalData: encoder.encode(requestId) },
        aesKey,
        encoder.encode(plaintext),
    );
    const blob: EncryptedChatHistory = {
        v: CHAT_CRYPTO_VERSION,
        alg: CHAT_CRYPTO_ALG,
        epk: bytesToBase64(sourcePublicRaw),
        iv: bytesToBase64(iv),
        ct: bytesToBase64(new Uint8Array(ciphertext)),
    };

    return JSON.stringify(blob);
}

/** Target side. Throws on an unknown version, a wrong key, a different requestId or tampering. */
export async function decryptChatHistory(
    serialized: string,
    keyPair: ChatKeyPair,
    requestId: string,
): Promise<string> {
    const blob = JSON.parse(serialized) as EncryptedChatHistory;
    if (blob?.v !== CHAT_CRYPTO_VERSION || blob.alg !== CHAT_CRYPTO_ALG) {
        throw new Error(`Unsupported chat history encryption: ${blob?.v} ${blob?.alg}`);
    }

    const sourcePublicRaw = base64ToBytes(blob.epk);
    const aesKey = await deriveAesKey(
        keyPair.privateKey,
        await importPublicKey(sourcePublicRaw),
        sourcePublicRaw,
        base64ToBytes(keyPair.publicKey),
        'decrypt',
    );
    const plaintext = await crypto.subtle.decrypt(
        {
            name: 'AES-GCM',
            iv: base64ToBytes(blob.iv),
            additionalData: encoder.encode(requestId),
        },
        aesKey,
        base64ToBytes(blob.ct),
    );

    return decoder.decode(plaintext);
}
